// frontend/src/pages/website/blog/HowToWinMoreJobsAsATradesperson.jsx
import React from "react";
import BlogPostLayout from "./BlogPostLayout";

export default function HowToWinMoreJobsAsATradesperson() {
    return (
        <BlogPostLayout
            slug="how-to-win-more-jobs-as-a-tradesperson"
            title="How to Win More Jobs as a Tradesperson Without Spending More on Ads"
            metaTitle="Win More Jobs as a Tradesperson | KonarCard"
            description="Five practical ways UK tradespeople win more work without paid advertising, and how to make word of mouth work harder for you."
            pillLabel="Getting more jobs"
            datePublished="2026-04-13"
            readTime="8 min read"
        >
            <p>
                Most tradespeople get their work through word of mouth. A happy customer tells a friend, the friend calls you, and the job is yours. It costs nothing and it produces some of the best customers you will ever work with, people who already trust you before you have even met them.
            </p>

            <p>
                The problem is that word of mouth is unpredictable. Some weeks the phone does not stop. Other weeks it goes quiet and there is nothing you can do to speed it up. You cannot scale something you cannot control.
            </p>

            <p>
                What you can do is make word of mouth work harder. Make it easier for happy customers to pass on your details. Make it easier for new customers to find you and trust you quickly. Make sure every job you complete has a chance of turning into three more.
            </p>

            <p>
                None of what follows requires a marketing budget, a website or any technical knowledge. These are practical habits that tradespeople across the UK are using right now to fill their diaries without spending a penny on ads.
            </p>

            <h2>1. Make It Effortless for Customers to Pass On Your Details</h2>

            <p>
                The most common reason a referral goes nowhere is not that the customer forgot about you, it is that they could not find your details when someone asked.
            </p>

            <p>
                Think about how referrals actually happen. A customer is talking to a neighbour at the garden fence. The neighbour mentions they need a plumber. Your customer wants to recommend you but cannot remember your number, cannot find the card you gave them six months ago and ends up saying "I'll find his number and send it to you", and then forgets.
            </p>

            <p>That referral is gone.</p>

            <p>
                The fix is to make sure your details are always findable. When you finish a job, tap your NFC card to the customer's phone and make sure they save your contact. When your link is saved in their phone, they can pass it on in thirty seconds without having to remember anything. They just find your name in their contacts and share the profile link.
            </p>

            <p>
                A customer who can refer you in thirty seconds will refer you far more often than one who has to go searching for a card they may or may not have kept.
            </p>

            <h2>2. Ask for Reviews at the Right Moment</h2>

            <p>
                Most tradespeople know they should ask for reviews. Very few do it consistently, and even fewer do it at the right time.
            </p>

            <p>
                The right time is while you are still on site, while the customer can see the finished work in front of them and while they are feeling good about how the job went. That feeling fades within hours. By the time you send a follow-up text the next day, the moment has passed and most customers will mean to leave a review but never quite get around to it.
            </p>

            <p>
                When you finish a job and the customer is happy, tap your card to their phone. Your profile opens with the review section right there. Ask them directly, not in a pushy way, just honestly. Something like: "If you were happy with the work, a quick review on here really helps me out." Most people are glad to help when asked at the right moment.
            </p>

            <p>
                A profile with ten genuine reviews from real customers is one of the most effective selling tools a tradesperson can have. New customers who land on your profile and see reviews from people like them, homeowners in the same area who had the same type of job done, are far more likely to call than customers who see an empty profile with just a phone number.
            </p>

            <h2>3. Show Your Work Before Anyone Asks</h2>

            <p>
                Most tradespeople wait for customers to ask to see previous work. The tradespeople winning the most jobs make sure customers see it before they even think to ask.
            </p>

            <p>
                Your profile should include photos of your best work, finished jobs, before and after where possible, a variety of project types. When a customer taps your card or scans your QR code, the first thing they see after your name and number is evidence of what you can do.
            </p>

            <p>
                This matters most in the trades where the quality of the finish is the main thing customers are buying. A decorator whose profile shows ten immaculate finished rooms does not need to talk about how good their work is. A landscaper whose profile shows transformed gardens sells the next job before they have said a word.
            </p>

            <p>
                Good photos do not require a professional photographer. A clear, well-lit photo taken on a modern smartphone is enough. Take one at the end of every job before you pack up. Within a few months you will have a portfolio that does your selling for you.
            </p>

            <h2>4. Follow Up After Every Job</h2>

            <p>
                Most tradespeople do the job, get paid and move on. The ones with the fullest diaries treat the end of the job as the start of the next customer relationship.
            </p>

            <p>
                A simple follow-up message a few days after a job is finished, checking that everything is working as expected and that the customer is happy, costs about thirty seconds and produces two things. First, it catches any minor issues before they become complaints. Second, it keeps you front of mind at exactly the point when the customer might be talking to friends and family about the work they have just had done.
            </p>

            <p>
                You do not need a complicated system for this. A short WhatsApp message works fine. Include your profile link at the bottom so the customer has an easy way to share your details or leave a review if they want to.
            </p>

            <h2>5. Look More Professional Than Your Competition at Every Touchpoint</h2>

            <p>
                In most trades the difference between getting the job and losing it to a competitor comes down to trust. Two tradespeople quoting on the same job at a similar price, the customer chooses the one they feel more confident about.
            </p>

            <p>
                Every touchpoint is an opportunity to build that confidence. The way you turn up on time. The way you explain the job clearly. The way you tidy up before you leave. And the way you hand over your details at the end.
            </p>

            <p>
                A tradesperson who produces a professional-looking card, taps it to the customer's phone and watches their profile open with photos, reviews and services listed is making a different impression than one who hands over a slightly battered paper card with a number on it.
            </p>

            <p>
                It is not about being flashy. It is about communicating that you are organised, that you care about your work and that you will be easy to deal with. Customers pay more for that and they recommend it more readily too.
            </p>

            <h2>How These Five Things Work Together</h2>

            <p>None of these tactics works in isolation as well as they work together.</p>

            <p>
                A tradesperson who makes it easy to pass on their details, asks for reviews at the right moment, shows their work upfront, follows up after every job and looks professional at every touchpoint is building something that compounds over time. Every job they complete has a higher chance of turning into referrals. Every referral arrives with more trust already built. Every review makes the next customer easier to convert.
            </p>

            <p>
                The common thread running through all five is making it easy, easy for customers to find your details, easy for them to see your work, easy for them to leave a review, easy for them to pass you on to someone else.
            </p>

            <p>
                A digital profile that loads in one tap and shows everything a customer needs to make a decision is the practical tool that ties all five habits together. It does not replace the work. It just makes sure the work you are already doing goes further.
            </p>
        </BlogPostLayout>
    );
}
